import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  EventService,
  JobsService,
  SettingsService,
  VmeInboxJobsService,
} from '../../../../services';
import { VMEJobScreenDatesPricesJob } from '../../../../interfaces/screen-specific/job-screen';
import moment from 'moment/moment';
import { JobScreenCommonService } from '../../common/job-screen-common.service';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { Subscription } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng';
import { Message } from '../../../../classes/Message';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { ISystemSettings } from '../../../../interfaces/system-settings';

@Component({
  selector: 'app-vme-job-screen-dates-prices',
  templateUrl: './vme-job-screen-dates-prices.component.html',
  styleUrls: [
    './vme-job-screen-dates-prices.component.scss',
    '../../common/job-screen-common.scss',
  ],
  providers: [ConfirmationService],
})
export class VMEJobScreenDatesPricesComponent implements OnInit, OnDestroy {
  private _jobId: number;
  job: VMEJobScreenDatesPricesJob;
  isShowChangeJobDetails: any;
  organisationTypeId = 1; // Implied by virtue of being a VME screen
  loadingJob: boolean = false;
  systemSettings: ISystemSettings;

  @Input('jobId')
  set jobId(jobId: number) {
    this._jobId = jobId;
    this.initData();
  }

  get jobId(): number {
    return this._jobId;
  }

  subscriptions: Subscription[] = [];

  constructor(
    private jobService: JobsService,
    private eventService: EventService,
    public commonServices: JobScreenCommonService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private settingsService: SettingsService,
    private inboxJobsService: VmeInboxJobsService,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: any) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnInit(): void {
    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  initData() {
    this.getJob(this.jobId);
  }

  getJob(jobId: number) {
    this.loadingJob = true;

    this.jobService
      .getForComponent(jobId, 'vme-job-screen-dates-prices')
      .then((job: VMEJobScreenDatesPricesJob) => {
        this.job = job;
        this.loadingJob = false;
      });
  }

  dialogClosed() {
    this.isShowChangeJobDetails = false;
  }

  isJobHeld = () => moment(this.job?.heldEndDate).isAfter(moment());
  showCustomerPremiums: boolean = false;
  showSupplierPremiums: boolean = false;

  firstCollectionDate(job: VMEJobScreenDatesPricesJob) {
    const firstTask = this.firstTask(job);
    return firstTask?.taskSupplierSchedule?.specifiedPickUpTime;
  }

  firstDeliveryDate(job: VMEJobScreenDatesPricesJob) {
    const firstTask = this.firstTask(job);
    return firstTask?.taskSupplierSchedule?.specifiedDropOffTime;
  }

  firstTask(job: VMEJobScreenDatesPricesJob) {
    job?.orders.forEach((order) => {
      order?.tasks.forEach((task) => {
        return task;
      });
    });
    return null;
  }

  get isManualReleaseFromHoldBlocked(): boolean {
    if (!this.job || !this.systemSettings || !this.job.heldEndDate) {
      return false;
    }

    const hasTransporterOrRecovery =
      this.job.orders?.some((order) =>
        order.tasks?.some((task) => task.isTransporterOrRecoveryMovement),
      ) ?? false;

    if (hasTransporterOrRecovery) return false;

    const manualReleaseFromHoldMinimumTimeMinutes =
      this.systemSettings.manualReleaseFromHoldMinimumTimeMinutes ?? 0;

    if (
      manualReleaseFromHoldMinimumTimeMinutes === 0 ||
      !moment(this.job.heldEndDate).isAfter(moment())
    ) {
      return false;
    }

    const remainingHoldTimeMinutes = moment(this.job.heldEndDate).diff(moment(), 'minutes');

    return remainingHoldTimeMinutes <= manualReleaseFromHoldMinimumTimeMinutes;
  }

  handleEvents(event: any) {
    if (event.event == 'job-details-changed') {
      this.getJob(this.jobId);
    }
  }

  protected grabStateChangeInProgress: boolean = false;

  async enableGrab() {
    const message = this.isJobHeld()
      ? 'The hold period for this job has not ended. Routes containing this job will be shown to suppliers, but the job will still not be visible in standard Job Search until the hold period ends. Please confirm you want to enable grab for this job.'
      : 'This job is no longer in held. Enabling Grab will make this job visible in both Route Search & Job Search. The job will be auto-directed. Please confirm you want to enable grab for this job.';

    this.confirmGrabAction(message, 'Enable Grab Confirmation', false);
  }

  async disableGrab() {
    this.confirmGrabAction(
      'Disabling grab for this job will make the job invisible in all Job & Route Inboxes. The job will not be auto-directed. Please confirm you want to disable grab for this job.',
      'Disable Grab Confirmation',
      true,
    );
  }

  private async updateGrabDisabledState(grabDisabled: boolean) {
    this.job.grabDisabled = grabDisabled;
    this.grabStateChangeInProgress = true;

    try {
      await this.jobService.updateGrabDisabledState({
        jobIds: [this.jobId],
        grabDisabled: this.job.grabDisabled,
      });
      this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    } catch (error) {
      this.messageService.add(Message.Error(error.error, 'Error updating grab disabled state.'));
      this.job.grabDisabled = !this.job.grabDisabled;
    } finally {
      this.grabStateChangeInProgress = false;
    }
  }

  private confirmGrabAction(message: string, header: string, grabDisabled: boolean) {
    this.confirmationService.confirm({
      message,
      header,
      accept: () => this.updateGrabDisabledState(grabDisabled),
      reject: () => {},
    });
  }

  releaseFromHold() {
    const manualReleaseFromHoldMinimumTimeMinutes =
      this.systemSettings?.manualReleaseFromHoldMinimumTimeMinutes ?? 0;

    //This should never actually run due to the Release from Hold button being disabled; however, I've added it as an additional safeguard.
    if (this.isManualReleaseFromHoldBlocked) {
      this.messageService.add(
        Message.Warning(
          `You are unable to release this job from hold as it is within the final ${manualReleaseFromHoldMinimumTimeMinutes} minutes of the hold period ending. The job will remain in hold until the hold period ends.`,
          'Unable to Release from Hold',
        ),
      );
      return;
    }

    this.inboxJobsService
      .releaseJobs([this.jobId])
      .then(() => {
        this.eventService.JobScreenEvent.emit({
          event: 'job-details-changed',
          data: { jobId: this.jobId },
          sender: 'releaseFromHold',
        });
      })
      .catch(() => {
        this.messageService.add(Message.Error('An error occurred releasing this job from hold'));
      });
  }

  showExtendHold = false;
  extendingHold = false;
  extendHoldDate: Date;
  minExtendHoldDate: Date;

  openExtendHold() {
    const heldUntil = moment(this.job.heldEndDate).seconds(0);

    // Extending only, so the current release time is the floor. Half an hour on keeps the first
    // selectable slot in step with the picker.
    this.minExtendHoldDate = heldUntil.clone().add(30, 'minutes').toDate();
    this.extendHoldDate = heldUntil.clone().add(1, 'day').toDate();
    this.showExtendHold = true;
  }

  doExtendHold() {
    this.extendingHold = true;

    this.inboxJobsService
      .holdJobs([this.jobId], this.extendHoldDate)
      .then(() => {
        this.eventService.JobScreenEvent.emit({
          event: 'job-details-changed',
          data: { jobId: this.jobId },
          sender: 'extendHold',
        });
      })
      .catch(() => {
        this.messageService.add(Message.Error('An error occurred extending the hold on this job'));
      })
      .finally(() => {
        this.extendingHold = false;
        this.showExtendHold = false;
      });
  }

  loadingOrBlank(noValue = 'N/A') {
    return this.loadingJob ? ' ' : noValue;
  }

  totalPremium(isSupplier: boolean) {
    // Return a total of all premiums for the supplier
    return this.job?.pricePremiums
      .filter((premium) => premium.isSupplierPremium === isSupplier)
      .reduce((acc, premium) => acc + premium.premium, 0);
  }

  showPremiums(job: VMEJobScreenDatesPricesJob, isSupplierPremium: boolean) {
    return job.pricePremiums?.filter((premium) => premium.isSupplierPremium === isSupplierPremium);
  }

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;
}
